/**
 * Rules & governance: hard fences + (legacy) autonomous-only controls.
 *
 * Product authoring today: claim quality = defaultDecisionMode; runs stay
 * propose+approve. Other (hard fence) checkboxes always shown.
 * GovernanceFencesEditor (allowed actions + limits) remains for legacy
 * autonomous_override rows but is not offered in authoring UI.
 *
 * Stored in `autonomy_fences` JSONB (`never` array). Override/kill-switch is
 * always on (not a setting).
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { StudioExecutionMode } from '@/types/aiStudioAgent';
import { FIELD_CLASS, LABEL_CLASS } from './constants';
import { FieldLabel } from './FieldLabel';

export const AUTONOMY_NEVER_OPTIONS = [
  'write_to_customer_SoR',
  'publish_without_log',
] as const;

/** Both Other fences are on unless the stored fence explicitly sets `never`. */
export const DEFAULT_AUTONOMY_NEVER: string[] = [...AUTONOMY_NEVER_OPTIONS];

export const AUTONOMY_ALLOWED_ACTIONS = ['reschedule_within_day'] as const;

export type AutonomyNeverOption = (typeof AUTONOMY_NEVER_OPTIONS)[number];
export type AutonomyAllowedAction = (typeof AUTONOMY_ALLOWED_ACTIONS)[number];

export interface AutonomyFencesConfig {
  never?: string[];
  allowed_actions?: string[];
  limits?: {
    max_cost_delta?: number | null;
    min_otd?: number | null;
  };
  /** Always true in product — kept for runtime shape, not shown in UI. */
  override?: 'always_available';
}

export function parseAutonomyFences(raw: Record<string, unknown>): AutonomyFencesConfig {
  const limitsRaw =
    raw.limits && typeof raw.limits === 'object' && !Array.isArray(raw.limits)
      ? (raw.limits as Record<string, unknown>)
      : {};
  // Missing `never` → product defaults (both checked). Explicit [] means author cleared them.
  const never = Array.isArray(raw.never)
    ? raw.never.map(String)
    : [...DEFAULT_AUTONOMY_NEVER];
  return {
    never,
    allowed_actions: Array.isArray(raw.allowed_actions)
      ? raw.allowed_actions.map(String)
      : [],
    limits: {
      max_cost_delta:
        typeof limitsRaw.max_cost_delta === 'number' ? limitsRaw.max_cost_delta : null,
      min_otd: typeof limitsRaw.min_otd === 'number' ? limitsRaw.min_otd : null,
    },
    override: 'always_available',
  };
}

export function serializeAutonomyFences(config: AutonomyFencesConfig): Record<string, unknown> {
  // Do not persist free-form `intent` — UI dropped it; leave legacy keys untouched on read.
  return {
    never: config.never ?? [...DEFAULT_AUTONOMY_NEVER],
    allowed_actions: config.allowed_actions ?? [],
    limits: {
      max_cost_delta: config.limits?.max_cost_delta ?? null,
      min_otd: config.limits?.min_otd ?? null,
    },
    override: 'always_available',
  };
}

/** Apply Other fence defaults for load/create/save. */
export function normalizeAutonomyFences(raw: Record<string, unknown>): Record<string, unknown> {
  return serializeAutonomyFences(parseAutonomyFences(raw));
}

interface GovernanceNeverFieldsetProps {
  value: Record<string, unknown>;
  disabled?: boolean;
  onChange: (next: Record<string, unknown>) => void;
}

/** Other (hard fences) — beside Default decision mode on the definition page. */
export const GovernanceNeverFieldset: React.FC<GovernanceNeverFieldsetProps> = ({
  value,
  disabled = false,
  onChange,
}) => {
  const { t } = useLanguage();
  const fences = useMemo(() => parseAutonomyFences(value), [value]);

  const toggleNever = (option: string, enabled: boolean) => {
    const set = new Set(fences.never ?? []);
    if (enabled) set.add(option);
    else set.delete(option);
    onChange(serializeAutonomyFences({ ...fences, never: [...set] }));
  };

  return (
    <fieldset>
      <FieldLabel as="span" tooltip={t('aiStudio.definition.governanceOtherHint')}>
        {t('aiStudio.definition.governanceOther')}
      </FieldLabel>
      <div className="flex flex-col gap-2">
        {AUTONOMY_NEVER_OPTIONS.map((option) => (
          <label
            key={option}
            className="flex items-center gap-2 text-base text-[var(--color-surface-text)]"
          >
            <input
              type="checkbox"
              checked={(fences.never ?? []).includes(option)}
              disabled={disabled}
              onChange={(e) => toggleNever(option, e.target.checked)}
            />
            {t(`aiStudio.definition.never.${option}`)}
          </label>
        ))}
      </div>
    </fieldset>
  );
};

interface GovernanceFencesEditorProps {
  executionMode: StudioExecutionMode;
  value: Record<string, unknown>;
  disabled?: boolean;
  onChange: (next: Record<string, unknown>) => void;
}

/**
 * Autonomous-only allowed actions + limits.
 * Other (hard fences) render separately beside Execution mode.
 */
export const GovernanceFencesEditor: React.FC<GovernanceFencesEditorProps> = ({
  executionMode,
  value,
  disabled = false,
  onChange,
}) => {
  const { t } = useLanguage();
  const fences = useMemo(() => parseAutonomyFences(value), [value]);
  const isAutonomous = executionMode === 'autonomous_override';

  if (!isAutonomous) {
    return null;
  }

  const patch = (partial: Partial<AutonomyFencesConfig>) => {
    const next: AutonomyFencesConfig = {
      ...fences,
      ...partial,
      limits: {
        ...fences.limits,
        ...(partial.limits ?? {}),
      },
    };
    onChange(serializeAutonomyFences(next));
  };

  const toggleAllowed = (option: string, enabled: boolean) => {
    const set = new Set(fences.allowed_actions ?? []);
    if (enabled) set.add(option);
    else set.delete(option);
    patch({ allowed_actions: [...set] });
  };

  return (
    <div className="flex flex-col gap-3">
      <fieldset>
        <FieldLabel as="span" tooltip={t('aiStudio.definition.allowedActionsHint')}>
          {t('aiStudio.definition.allowedActions')}
        </FieldLabel>
        <div className="flex flex-col gap-2">
          {AUTONOMY_ALLOWED_ACTIONS.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 text-base text-[var(--color-surface-text)]"
            >
              <input
                type="checkbox"
                checked={(fences.allowed_actions ?? []).includes(option)}
                disabled={disabled}
                onChange={(e) => toggleAllowed(option, e.target.checked)}
              />
              {t(`aiStudio.definition.allowedAction.${option}`)}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid gap-3">
        <div>
          <label className={LABEL_CLASS} htmlFor="def-max-cost-delta">
            {t('aiStudio.definition.limitMaxCostDelta')}
          </label>
          <input
            id="def-max-cost-delta"
            type="number"
            min={0}
            step={100}
            className={FIELD_CLASS}
            value={fences.limits?.max_cost_delta ?? ''}
            disabled={disabled}
            placeholder={t('aiStudio.definition.limitMaxCostDeltaPlaceholder')}
            onChange={(e) => {
              const raw = e.target.value;
              patch({
                limits: {
                  max_cost_delta: raw === '' ? null : Number(raw),
                  min_otd: fences.limits?.min_otd ?? null,
                },
              });
            }}
          />
        </div>
        <div>
          <label className={LABEL_CLASS} htmlFor="def-min-otd">
            {t('aiStudio.definition.limitMinOtd')}
          </label>
          <input
            id="def-min-otd"
            type="number"
            min={0}
            max={1}
            step={0.01}
            className={FIELD_CLASS}
            value={fences.limits?.min_otd ?? ''}
            disabled={disabled}
            placeholder={t('aiStudio.definition.limitMinOtdPlaceholder')}
            onChange={(e) => {
              const raw = e.target.value;
              patch({
                limits: {
                  max_cost_delta: fences.limits?.max_cost_delta ?? null,
                  min_otd: raw === '' ? null : Number(raw),
                },
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};
